import type { UseFetchFnMeta } from '#/hooks/useFetchFn';
import { type FetchUrlOptions, useFetchUrl } from '#/hooks/useFetchUrl';
import type { HorizonAppLoaderContext } from '#/horizon/types/horizon-app-loader-context';
import { isRenderingServer } from '#/rendering/state/renderingState';
import { getJson } from '#/utils/http/getJson';
import { postJson } from '#/utils/http/postJson';
import { log } from '#/utils/logging';

type FetchEndpointUrlFactory<TArgs extends unknown[], TUrl extends string> = (
  ...args: TArgs
) =>
  | TUrl
  | {
      path: string;
      searchParams: Record<
        string,
        string | string[] | undefined | boolean | null
      >;
    }
  | undefined;

type FetchEndpointOptionsFactory<TArgs extends unknown[]> = (
  ...args: TArgs
) => FetchUrlOptions;

type FetchEndpointResponseMapper<TMappedResponse, TResponse> = (
  response: TResponse,
) => TMappedResponse;

export class FetchEndpoint<
  TArgs extends unknown[],
  TUrl extends string,
  TMappedResponse,
  TResponse,
> {
  urlFactory: FetchEndpointUrlFactory<TArgs, TUrl>;

  optionsFactory: FetchEndpointOptionsFactory<TArgs> | undefined;

  mapResponse: FetchEndpointResponseMapper<TMappedResponse, TResponse>;

  constructor(args: {
    urlFactory: FetchEndpointUrlFactory<TArgs, TUrl>;
    optionsFactory?: FetchEndpointOptionsFactory<TArgs>;
    mapResponse: FetchEndpointResponseMapper<TMappedResponse, TResponse>;
  }) {
    this.urlFactory = args.urlFactory;
    this.optionsFactory = args.optionsFactory;
    this.mapResponse = args.mapResponse;
  }

  private url(...args: TArgs): string | undefined {
    const result = this.urlFactory(...args);
    if (!result || typeof result === 'string') return result;

    const searchParamsObject = new URLSearchParams();
    for (const [key, value] of Object.entries(result.searchParams)) {
      if (value !== false && value !== null && typeof value !== 'undefined') {
        if (Array.isArray(value)) {
          value.forEach((item) => searchParamsObject.append(key, item));
        } else {
          searchParamsObject.append(key, value as string);
        }
      }
    }

    const searchParamsString = searchParamsObject.toString();

    return searchParamsString
      ? `${result.path}?${searchParamsString}`
      : result.path;
  }

  createSsrApiDataFn() {
    return (
      ssrApiData: Partial<Record<string, unknown>>,
      ...args: TArgs
    ): TMappedResponse | undefined => {
      const url = this.url(...args);
      const data = url ? (ssrApiData[url] as TResponse | undefined) : undefined;

      if (data) return this.mapResponse(data);
    };
  }

  createClientFetchFn() {
    return async (...args: TArgs): Promise<TMappedResponse> => {
      const url = this.url(...args);
      const options = this.optionsFactory?.(...args);

      if (!url) throw new Error('Cannot fetch with undefined URL');

      // This is a horizon server request
      if (isRenderingServer()) {
        const message = `Attempting to use client-only fetch during SSR. Url ${url}`;

        // Besides throwing, also log so it's easier to see what's happening
        // Sometimes we try/catch these fetch calls to account for 404
        log.error(message);
        throw new Error(message);
      }

      const response = await (options?.method === 'post'
        ? postJson<TResponse>(url, options.body)
        : getJson<TResponse>(url));

      return this.mapResponse(response);
    };
  }

  createIsomorphicFetchFn() {
    return (requestInfo: {
      request: Request;
      context: HorizonAppLoaderContext;
    }) => {
      const { request, context } = requestInfo;

      return (...args: TArgs): Promise<TMappedResponse> => {
        const url = this.url(...args);
        if (!url) throw new Error('Cannot fetch with undefined URL');

        const options = this.optionsFactory?.(...args);
        const existingFetchPromise = context.fetchPromises?.[url];
        if (options?.method !== 'post' && existingFetchPromise) {
          return existingFetchPromise as unknown as Promise<TMappedResponse>;
        }

        if (!isRenderingServer()) {
          context.fetchPromises ??= {};

          const promise = (
            options?.method === 'post'
              ? postJson<TResponse>(url, options.body)
              : getJson<TResponse>(url)
          ).then(this.mapResponse);

          context.fetchPromises[url] = promise;
          return promise;
        }

        if (context.type === 'hypernova') {
          const data = context.hypernovaSsrApiData?.[url] as
            | TResponse
            | undefined;

          if (!data)
            throw new Error(`Data for ${url} is not available during SSR`);

          return Promise.resolve(this.mapResponse(data));
        }

        // Horizon server
        const cookieHeader = request.headers.get('Cookie');

        const xForwardedForHeader =
          request.headers.get('X-Forwarded-For') ?? '';

        const previousForwardedFor = xForwardedForHeader
          .split(',')
          .map((ip) => ip.trim());

        const promise = fetch(`${context.horizonServerEnv.backend.url}${url}`, {
          headers: {
            Cookie: cookieHeader ?? '',
            'X-Forwarded-For': [context.clientIp, ...previousForwardedFor].join(
              ', ',
            ),
            'X-Horizon-Use': 'true',
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          method: options?.method,
          // oxlint-disable eslint-plugin-unicorn/no-invalid-fetch-options
          body:
            options?.method === 'post'
              ? JSON.stringify(options.body)
              : undefined,
        })
          .then((response) => response.json())
          .then((json) =>
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.mapResponse(json),
          );

        context.fetchPromises[url] = promise;

        return promise;
      };
    };
  }

  createUseFetchHook() {
    return (
      ...args: TArgs
    ): [TMappedResponse | undefined, UseFetchFnMeta<TMappedResponse>] => {
      const url = this.url(...args);
      const options = this.optionsFactory?.(...args);

      return useFetchUrl({
        url,
        options,
        mapResponse: this.mapResponse,
      });
    };
  }
}
